import { useQuery } from "@tanstack/react-query";
import { isApiError, queueApi } from "@bw/shared/api";
import type { CourtAssignmentResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The player's court assignment for a queue entry
 * (GET /queue/{entry_id}/assignment → CourtAssignmentResponse).
 *
 * Backed by the REAL `queueApi.getAssignment` on the `@bw/shared` queue spine —
 * no optional-method probe, cast, or second court-list lookup. Returns the
 * player's OWN assignment with a required, Facility-owned court_name. The
 * just-committed assignment can briefly be unreadable after response loss.
 * Retry bounded transient 404/5xx/network failures before the assigned page
 * degrades honestly to "Finding you a court"; authorization failures are final.
 *
 * `enabled` is gated by the caller so the read only fires once the entry is
 * actually assigned (or its status is still unknown).
 */
export function useAssignment(entryId: string, enabled: boolean) {
  return useQuery<CourtAssignmentResponse>({
    queryKey: queryKeys.dropin.queueAssignment(entryId),
    queryFn: () => queueApi.getAssignment(entryId),
    enabled: enabled && Boolean(entryId),
    retry: (failureCount, error) =>
      failureCount < 2 &&
      (!isApiError(error) || error.status === 404 || error.status >= 500),
    retryDelay: (attempt) => Math.min(250 * 2 ** attempt, 1_000),
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}
